import dompurify from 'dompurify';

const sanitizer = dompurify.sanitize;

// if allowHtml is set to true, HTML markup will be allowed (to be used in content like email editors)
// if false, the input is fully stripped so that nothing but <br> tags are allowed (to be used in showing simple data like relation name)
export function sanitize(input, allowHtml) {
  if (allowHtml) {
    const sanitized = sanitizer(input, {
      WHOLE_DOCUMENT: true,
      USE_PROFILES: { html: true },
      ADD_ATTR: ['target'],
    });
    return sanitized;
  }

  return sanitizer(input, {
    ALLOWED_TAGS: ['br'],
  });
}

// The element holding the first visible text of the mail.
function firstTextElement(body) {
  const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node.textContent.trim()) return node.parentElement;
    node = walker.nextNode();
  }
  return null;
}

// Nearest self-or-ancestor inline value for a style property, stopping at <body>.
function inheritedInlineStyle(el, body, styleProp) {
  for (let cur = el; cur && cur !== body; cur = cur.parentElement) {
    if (cur.style[styleProp]) return cur.style[styleProp];
  }
  return '';
}

// Detects the font used across the template's inline styles. Returns null when
// fonts are mixed or absent, since there is no single font to promote.
// Only styles that wrap actual text count, and the mail's first text block must use the
// detected font: the merged body also contains the company signature, so a font that only
// appears on a trailing fragment (e.g. a styled disclaimer) is not the template's font —
// a mail that starts unstyled was authored at the editor default and must stay that way.
function detectTemplateFont(body) {
  const fontFamilies = new Set();
  const fontSizes = new Set();

  body.querySelectorAll('*').forEach(el => {
    if (!el.textContent.trim()) return;
    if (el.style.fontFamily) fontFamilies.add(el.style.fontFamily);
    if (el.style.fontSize) fontSizes.add(el.style.fontSize);
  });

  const firstText = firstTextElement(body);
  if (!firstText) return null;

  const fontFamily =
    fontFamilies.size === 1 && inheritedInlineStyle(firstText, body, 'fontFamily')
      ? [...fontFamilies][0]
      : null;
  const fontSize =
    fontSizes.size === 1 && inheritedInlineStyle(firstText, body, 'fontSize')
      ? [...fontSizes][0]
      : null;

  if (!fontFamily && !fontSize) return null;
  return { fontFamily, fontSize };
}

function withBodyFont(html, getFont) {
  if (!html || typeof html !== 'string') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const { body } = doc;
  if (!body) return html;

  const font = getFont(body);
  if (!font) return html;

  let changed = false;
  if (font.fontFamily && !body.style.fontFamily) {
    body.style.fontFamily = font.fontFamily;
    changed = true;
  }
  if (font.fontSize && !body.style.fontSize) {
    body.style.fontSize = font.fontSize;
    changed = true;
  }
  if (!changed) return html;

  const doctype = doc.doctype ? `<!DOCTYPE ${doc.doctype.name}>` : '';
  return doctype + doc.documentElement.outerHTML;
}

// Promotes the font of a template-based mail to an inline style on <body>, so new
// paragraphs (which CKEditor creates without font spans) inherit it — both in the
// editor and in the mail the recipient receives.
export function applyTemplateFontToBody(html) {
  return withBodyFont(html, detectTemplateFont);
}

// Gives a free mail, which has no template font, a font on <body> the same way, so the typed
// text is sent with a font instead of the recipient's default. The user's own choice comes
// first, then the font of the signature the mail starts with, then the application default.
export function applyFreeMailFontToBody(html, userFont, defaultFont) {
  return withBodyFont(html, body => {
    const signatureFont = detectTemplateFont(body) || {};
    return {
      fontFamily: userFont?.fontFamily || signatureFont.fontFamily || defaultFont.fontFamily,
      fontSize: userFont?.fontSize || signatureFont.fontSize || defaultFont.fontSize,
    };
  });
}
